/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { ArrowRight, Clock3, FileWarning, Inbox, Plus, Scale, Search, ShieldAlert, Wand2, Unplug } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { api } from '../../api';
import type { RegistryTransactionRow } from '../../api/areas/registry';
import type {
  ConnectionDetail,
  ConnectionKind,
  ConnectionProtocol,
  ConnectionStatus,
  ConnectionSummary,
  CreateConnectionBody,
  WriteResult,
} from '../../api/areas/integrations';
import { Badge, type Tone } from '../../components/Badge';
import { Callout, DataTable, Field, FilterBar, type Column } from '../../components/blocks';
import { DomainLink } from '../../components/Ids';
import { FactRows, TaskList, TaskRow, TwoColumn } from '../../components/flow';
import { DataError } from '../../components/DataError';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { useRetained, useWrite } from '../../components/Write';
import { formatCount, formatMoney } from '../../lib/format';
import { useLabels } from '../../lib/labels';
import { useApi } from '../../lib/useApi';
import { OutcomeBadge, When as RegistryWhen } from '../registry/shared';
import { CONNECTION_CHANGE_KINDS, PendingChangesPanel } from './changes';
import {
  ChangeDialog,
  ChannelBadge,
  ChannelGuide,
  channelOf,
  ConfirmCheck,
  DeploymentNote,
  IntegrationError,
  Panel,
  ReasonField,
  SelectField,
  tx,
  useIntegrationContext,
  WithIntegrationContext,
  WriteOutcome,
} from './shared';
import { CONNECTION_PRESETS, WEBNIC_ADAPTER, WEBNIC_BASE_URLS, webnicAvailable, type ConnectionPreset } from './presets';
import { WebnicAllowListNote } from './connection/webnic';
import { LastTest } from './tests';

const ALL = '__all__';

/** `/settings/registries` — every registry and partner-registrar connection, per environment. */
export function ConnectionsPage() {
  const { t } = useTranslation('integrations');
  useDocumentTitle(t('connections.title'));
  const [creating, setCreating] = useState(false);
  return (
    <>
      <PageHeader
        title={t('connections.title')}
        subtitle={t('connections.subtitle')}
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus /> {t('connections.add')}
          </Button>
        }
      />
      <WithIntegrationContext>
        <ConnectionsView creating={creating} setCreating={setCreating} />
      </WithIntegrationContext>
    </>
  );
}

function ConnectionsView({ creating, setCreating }: { creating: boolean; setCreating: (v: boolean) => void }) {
  const { t } = useTranslation('integrations');
  const [params, setParams] = useSearchParams();
  const [tick, setTick] = useState(0);
  const kind = (params.get('kind') ?? '') as ConnectionKind | '';
  const protocol = (params.get('protocol') ?? '') as ConnectionProtocol | '';
  const status = (params.get('status') ?? '') as ConnectionStatus | '';
  const q = params.get('q') ?? '';
  const [search, setSearch] = useState(q);
  const result = useApi(
    (signal) => api.listConnections({ kind: kind || undefined, protocol: protocol || undefined, status: status || undefined, q: q || undefined }, { signal }),
    [kind, protocol, status, q, tick],
  );
  const data = useRetained(result.data);
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const reload = () => setTick((n) => n + 1);
  const facts = useCardFacts(tick);
  const live = (data?.items ?? []).filter((c) => c.status !== 'RETIRED');
  const ready = live.filter((c) => healthOf(c) === 'READY').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {data ? (
          <p className="flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground">
            <Badge tone={ready === live.length ? 'ok' : 'warn'}>{t('health.summary', { ready, attention: live.length - ready })}</Badge>
            {t('health.summaryHint')}
          </p>
        ) : (
          <span />
        )}
        <DeploymentNote />
      </div>
      <details className="rounded-lg border px-4 py-3 text-sm">
        <summary className="cursor-pointer font-medium">{t('health.explainTitle')}</summary>
        <div className="mt-3 space-y-3">
          <p>{t('connections.explain')}</p>
          <ChannelGuide />
          <p>
            <Link to="/settings/tlds" className="font-medium text-primary hover:underline">
              {t('connections.toTlds')}
            </Link>
          </p>
        </div>
      </details>
      <PendingChangesPanel kinds={CONNECTION_CHANGE_KINDS} refreshKey={tick} onChanged={reload} />

      <div>
        <FilterBar>
          <SelectField
            label={t('connections.filter.kind')}
            value={kind || ALL}
            onChange={(v) => set('kind', v === ALL ? '' : v)}
            options={[{ value: ALL, label: t('common.all') }, { value: 'REGISTRY', label: t('kind.REGISTRY') }, { value: 'UPSTREAM_REGISTRAR', label: t('kind.UPSTREAM_REGISTRAR') }]}
            className="min-w-44"
          />
          <SelectField
            label={t('connections.filter.protocol')}
            value={protocol || ALL}
            onChange={(v) => set('protocol', v === ALL ? '' : v)}
            options={[{ value: ALL, label: t('common.all') }, { value: 'EPP', label: t('protocol.EPP') }, { value: 'REST', label: t('protocol.REST') }]}
            className="min-w-44"
          />
          <SelectField
            label={t('connections.filter.status')}
            value={status || ALL}
            onChange={(v) => set('status', v === ALL ? '' : v)}
            options={[
              { value: ALL, label: t('common.all') },
              { value: 'ACTIVE', label: t('connectionStatus.ACTIVE') },
              { value: 'SUSPENDED', label: t('connectionStatus.SUSPENDED') },
              { value: 'RETIRED', label: t('connectionStatus.RETIRED') },
            ]}
            className="min-w-40"
          />
          <form
            className="flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              set('q', search.trim());
            }}
          >
            <Field label={t('connections.filter.search')}>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('connections.filter.searchPlaceholder')} className="w-56" />
            </Field>
            <Button type="submit" variant="outline" size="icon" aria-label={t('connections.filter.search')}>
              <Search />
            </Button>
          </form>
        </FilterBar>
        {result.status === 'error' ? (
          <DataError error={result.error} onRetry={result.reload} title={t('connections.loadFailed')} />
        ) : !data ? (
          <Loading label={t('connections.loading')} rows={4} />
        ) : data.items.length === 0 ? (
          <EmptyState title={t('connections.empty')}>{t('connections.emptyHint')}</EmptyState>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 [&>*]:min-w-0">
            {data.items.map((c) => (
              <ConnectionCard key={c.id} connection={c} facts={facts} />
            ))}
          </div>
        )}
      </div>

      {data ? <TwoColumn main={<RecentCommands />} side={<Attention connections={data.items} />} /> : null}

      <CreateConnectionDialog open={creating} onOpenChange={setCreating} onCreated={reload} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Connection cards and the health around them (reference screen 16)

type Health = 'READY' | 'NOT_READY' | 'TEST_FAILED' | 'SUSPENDED' | 'RETIRED' | 'NOT_CONFIGURED';
const HEALTH_TONE: Record<Health, Tone> = { READY: 'ok', NOT_READY: 'warn', TEST_FAILED: 'crit', SUSPENDED: 'warn', RETIRED: 'neutral', NOT_CONFIGURED: 'neutral' };

export function healthOf(c: ConnectionSummary): Health {
  if (c.status === 'RETIRED') return 'RETIRED';
  if (c.status === 'SUSPENDED') return 'SUSPENDED';
  if (!c.settings) return 'NOT_CONFIGURED';
  if (c.settings.last_test?.outcome === 'FAILED') return 'TEST_FAILED';
  return c.settings.ready ? 'READY' : 'NOT_READY';
}

/** One word for a connection's state: ready, not ready, last check failed, suspended, … */
export function ConnectionHealthBadge({ connection }: { connection: ConnectionSummary }) {
  const { t } = useTranslation('integrations');
  const health = healthOf(connection);
  return <Badge tone={HEALTH_TONE[health]}>{t(`health.state.${health}`)}</Badge>;
}

/** Commands of the last hours of one connection (registry transactions, all traffic classes). */
interface Traffic {
  total: number;
  unresolved: number;
  slowest: number | null;
}

/** What the cards read besides the connection list; each part is optional (permissions differ). */
interface CardFacts {
  zones: Map<string, string[]>;
  traffic: Map<string, Traffic> | null;
  hours: number;
}

function useCardFacts(tick: number): CardFacts {
  const channels = useApi((signal) => api.listTldChannels({ limit: 500 }, { signal }), [tick]);
  const sessions = useApi((signal) => api.getEppSessions({}, { signal }), [tick]);
  const zones = new Map<string, string[]>();
  for (const r of channels.data?.items ?? []) {
    const list = zones.get(r.current.connection.id) ?? [];
    list.push(r.unicode_zone || r.zone);
    zones.set(r.current.connection.id, list);
  }
  let traffic: Map<string, Traffic> | null = null;
  if (sessions.data) {
    traffic = new Map();
    for (const row of sessions.data.traffic) {
      const key = row.registry_code.toLowerCase();
      const t = traffic.get(key) ?? { total: 0, unresolved: 0, slowest: null };
      t.total += row.total;
      t.unresolved += row.unresolved;
      if (row.max_latency_ms !== null) t.slowest = Math.max(t.slowest ?? 0, row.max_latency_ms);
      traffic.set(key, t);
    }
  }
  return { zones, traffic, hours: sessions.data?.traffic_window_hours ?? 24 };
}

/** The provider's balance, when its account interface offers one (the server caches it a minute). */
function CardBalance({ connectionId }: { connectionId: string }) {
  const { t } = useTranslation('integrations');
  const r = useApi((signal) => api.getAccountBalance(connectionId, { signal }), [connectionId]);
  if (r.status === 'loading') return <span className="text-muted-foreground">…</span>;
  if (r.status === 'error' || !r.data.amount || !r.data.currency) return <span className="text-muted-foreground">{t('health.balanceError')}</span>;
  return <span className="tabular">{formatMoney({ amount: r.data.amount, currency: r.data.currency })}</span>;
}

function Metric({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-semibold tracking-[0.08em] text-muted-foreground uppercase">{label}</div>
      <div className="tabular text-lg font-semibold">{children}</div>
    </div>
  );
}

function ConnectionCard({ connection: c, facts }: { connection: ConnectionSummary; facts: CardFacts }) {
  const { t } = useTranslation('integrations');
  const to = `/settings/registries/${encodeURIComponent(c.code)}`;
  const zones = facts.zones.get(c.id) ?? [];
  const traffic = facts.traffic?.get(c.code.toLowerCase());
  const cert = c.protocol === 'EPP' ? c.settings?.certificate : null;
  const tlds = c.tld_counts.configured + c.tld_counts.by_default;
  return (
    <Card className={cn('gap-4 py-5 shadow-xs', c.status === 'RETIRED' && 'opacity-70')}>
      <CardHeader className="gap-2 px-5">
        <div className="flex min-w-0 flex-wrap items-start gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-accent font-semibold text-accent-foreground" aria-hidden="true">
            {c.code.slice(0, 2).toUpperCase()}
          </span>
          <div className="min-w-0 flex-1 basis-40">
            <Link to={to} className="text-base font-semibold break-words hover:text-primary hover:underline">
              {c.name}
            </Link>
            <div className="truncate text-xs text-muted-foreground" title={zones.map((z) => `.${z}`).join(', ')}>
              {zones.length ? `${zones.slice(0, 3).map((z) => `.${z}`).join(', ')}${zones.length > 3 ? ` ${t('health.moreZones', { count: zones.length - 3 })}` : ''}` : t('health.noZones')} ·{' '}
              <span className="font-mono">{c.code}</span>
            </div>
          </div>
          <ConnectionHealthBadge connection={c} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4 px-5">
        <div className="grid grid-cols-3 gap-3 border-b pb-4">
          {/* No row for the connection in the window: no command (0); no traffic read at all: not known (—). */}
          <Metric label={t('health.commands', { hours: facts.hours })}>{facts.traffic ? formatCount(traffic?.total ?? 0) : '—'}</Metric>
          <Metric label={t('health.slowest')}>{traffic?.slowest != null ? `${formatCount(traffic.slowest)} ms` : '—'}</Metric>
          <Metric label={t('health.unresolved')}>
            <span className={cn(traffic?.unresolved ? 'text-serious' : undefined)}>{facts.traffic ? formatCount(traffic?.unresolved ?? 0) : '—'}</span>
          </Metric>
        </div>
        <FactRows
          rows={[
            [t('connections.filter.protocol'), <ChannelBadge key="ch" channel={channelOf(c.kind, c.protocol)} short />],
            c.account_features?.balance && c.settings?.ready ? [t('health.balance'), <CardBalance key="b" connectionId={c.id} />] : null,
            cert
              ? [
                  t('health.certificate'),
                  cert.configured && cert.days_remaining !== null ? (
                    <span key="cert" className={cn(cert.days_remaining < 30 && 'font-medium text-warning')}>
                      {t('health.certificateDays', { count: cert.days_remaining })}
                    </span>
                  ) : (
                    <span key="cert" className="text-warning">
                      {t('health.noCertificate')}
                    </span>
                  ),
                ]
              : null,
            c.settings ? [t('health.lastTest'), <LastTest key="lt" test={c.settings.last_test} />] : null,
          ]}
        />
        {c.adapter_status === 'NOT_AVAILABLE_NEEDS_SPECIFICATION' ? (
          <p className="rounded-md border border-warning/30 bg-warning-soft px-2 py-1.5 text-xs">{t('adapterStatus.NOT_AVAILABLE_NEEDS_SPECIFICATION')}</p>
        ) : null}
        <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3 text-xs text-muted-foreground">
          <span className="flex flex-wrap gap-x-3 gap-y-1">
            <span>{t('connections.tlds', { count: tlds })}</span>
            <span>{t('connections.domains', { count: c.domains_held, formatted: formatCount(c.domains_held) })}</span>
            {c.open_changes ? <span className="font-medium text-primary">{t('connections.openChanges', { count: c.open_changes })}</span> : null}
          </span>
          <Button asChild size="sm" variant="outline">
            <Link to={to}>
              {t('health.detail')} <ArrowRight />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** The latest commands to every registry and partner registrar (no payloads). */
function RecentCommands() {
  const { t } = useTranslation('integrations');
  const L = useLabels();
  const result = useApi((signal) => api.listRegistryTransactions({ limit: 8 }, { signal }), []);
  const columns: Column<RegistryTransactionRow>[] = [
    { key: 'at', header: t('health.recent.col.at'), cell: (r) => <RegistryWhen at={r.started_at} /> },
    { key: 'conn', header: t('health.recent.col.connection'), className: 'whitespace-normal', cell: (r) => <span className="text-[13px]">{r.registry_name}</span> },
    {
      key: 'cmd',
      header: t('health.recent.col.command'),
      cell: (r) => (
        <Link to={`/registry/transactions/${encodeURIComponent(r.id)}`} className="font-medium text-primary hover:underline">
          {L.eppCommand(r.operation)}
        </Link>
      ),
    },
    { key: 'obj', header: t('health.recent.col.object'), cell: (r) => (r.object_type === 'DOMAIN' && r.object_key ? <DomainLink keyOrName={r.object_key} /> : <span className="text-muted-foreground">—</span>) },
    { key: 'res', header: t('health.recent.col.result'), cell: (r) => <OutcomeBadge value={r.state} /> },
  ];
  return (
    <Panel
      title={t('health.recent.title')}
      description={t('health.recent.description')}
      flush
      actions={
        <Button asChild size="sm" variant="outline">
          <Link to="/ops?tab=registry&sub=transactions">{t('health.recent.open')}</Link>
        </Button>
      }
    >
      {result.status === 'loading' ? (
        <div className="px-5 pb-5">
          <Loading rows={3} />
        </div>
      ) : result.status === 'error' ? (
        <p className="px-5 pb-5 text-sm text-muted-foreground">{t('health.recent.hidden')}</p>
      ) : (
        <DataTable columns={columns} rows={result.data.items} rowKey={(r) => r.id} empty={<EmptyState title={t('health.recent.empty')} />} />
      )}
    </Panel>
  );
}

/** What needs someone: expiring certificates, connections not ready, commands without a result,
 * differences with the registry, registry messages, changes waiting. */
function Attention({ connections }: { connections: readonly ConnectionSummary[] }) {
  const { t } = useTranslation('integrations');
  const registries = useApi((signal) => api.listRegistries({ signal }), []);
  const live = connections.filter((c) => c.status !== 'RETIRED');
  const expiring = live.filter((c) => c.protocol === 'EPP' && c.settings?.certificate?.days_remaining != null && c.settings.certificate.days_remaining < 30);
  const notReady = live.filter((c) => c.settings && !c.settings.ready);
  const changes = live.reduce((n, c) => n + c.open_changes, 0);
  const sum = (k: 'unresolved_transactions' | 'open_reconciliation_issues' | 'unprocessed_poll_messages') => (registries.data?.items ?? []).reduce((n, r) => n + r[k], 0);
  const known = registries.status === 'success';
  const rows = [
    expiring.length && { key: 'cert', icon: ShieldAlert, tone: 'warn' as Tone, title: t('health.attention.certificates'), hint: t('health.attention.certificatesHint', { list: expiring.map((c) => c.name).join(', ') }), count: expiring.length, to: `/settings/registries/${encodeURIComponent(expiring[0]?.code ?? '')}/certificates` },
    notReady.length && { key: 'ready', icon: Unplug, tone: 'warn' as Tone, title: t('health.attention.notReady'), hint: t('health.attention.notReadyHint', { list: notReady.map((c) => c.name).join(', ') }), count: notReady.length, to: undefined },
    known && sum('unresolved_transactions') && { key: 'unknown', icon: Clock3, tone: 'serious' as Tone, title: t('health.attention.unresolved'), hint: t('health.attention.unresolvedHint'), count: sum('unresolved_transactions'), to: '/ops?tab=registry&sub=transactions&state=UNRESOLVED' },
    known && sum('open_reconciliation_issues') && { key: 'drift', icon: Scale, tone: 'crit' as Tone, title: t('health.attention.drift'), hint: t('health.attention.driftHint'), count: sum('open_reconciliation_issues'), to: '/ops?tab=registry&sub=reconciliation' },
    known && sum('unprocessed_poll_messages') && { key: 'poll', icon: Inbox, tone: 'info' as Tone, title: t('health.attention.messages'), hint: t('health.attention.messagesHint'), count: sum('unprocessed_poll_messages'), to: '/ops?tab=registry&sub=poll&status=UNPROCESSED' },
    changes && { key: 'changes', icon: FileWarning, tone: 'pending' as Tone, title: t('health.attention.changes'), hint: t('health.attention.changesHint'), count: changes, to: undefined },
  ].filter((r): r is Exclude<typeof r, 0 | false> => Boolean(r));
  return (
    <Panel title={t('health.attention.title')} description={t('health.attention.description')}>
      {rows.length ? (
        <TaskList>
          {rows.map((r) => (
            <TaskRow key={r.key} icon={r.icon} tone={r.tone} title={r.title} hint={r.hint} count={formatCount(r.count)} to={r.to} />
          ))}
        </TaskList>
      ) : (
        <p className="text-sm text-muted-foreground">{t('health.attention.none')}</p>
      )}
      <Callout tone="info" title={t('health.breaker.title')} className="mt-4">
        {t('health.breaker.body')}
      </Callout>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Create

function ChoiceCard({ selected, onSelect, title, body, disabled }: { selected: boolean; onSelect: () => void; title: ReactNode; body: ReactNode; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
      className={cn('grid gap-1 rounded-lg border p-3 text-left text-sm transition-colors hover:bg-muted/50 disabled:opacity-50', selected && 'border-primary bg-accent/40 ring-1 ring-primary')}
    >
      <span className="font-medium">{title}</span>
      <span className="text-xs text-muted-foreground">{body}</span>
    </button>
  );
}

export function CreateConnectionDialog({ open, onOpenChange, onCreated }: { open: boolean; onOpenChange: (v: boolean) => void; onCreated: () => void }) {
  const { t } = useTranslation('integrations');
  return (
    <ChangeDialog open={open} onOpenChange={onOpenChange} title={t('create.title')} description={t('create.description')} wide>
      <CreateConnectionForm onClose={() => onOpenChange(false)} onCreated={onCreated} />
    </ChangeDialog>
  );
}

function CreateConnectionForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  const navigate = useNavigate();
  const [kind, setKind] = useState<ConnectionKind>('REGISTRY');
  const [protocol, setProtocol] = useState<ConnectionProtocol>('EPP');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [eppAdapter, setEppAdapter] = useState<'standard-epp' | 'vnnic-epp'>('standard-epp');
  const [restAdapter, setRestAdapter] = useState('none');
  const [spec, setSpec] = useState('');
  const [legalName, setLegalName] = useState('');
  const [tradeName, setTradeName] = useState('');
  const [iana, setIana] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [preset, setPreset] = useState<ConnectionPreset['key'] | null>(null);
  const write = useWrite((body: CreateConnectionBody, opts) => api.createConnection(body, opts));
  const presets = CONNECTION_PRESETS.filter((p) => (p.key === 'WEBNIC' ? webnicAvailable(ctx) : true));
  const applyPreset = (p: ConnectionPreset) => {
    setPreset(p.key);
    setKind(p.kind);
    setProtocol(p.protocol);
    if (p.protocol === 'EPP') setEppAdapter(p.adapter as 'standard-epp' | 'vnnic-epp');
    else setRestAdapter(p.adapter);
    setCode(p.code);
    setName(p.name);
    setSpec(p.spec);
    setLegalName(p.upstream?.legal_name ?? '');
    setTradeName(p.upstream?.trade_name ?? '');
    setIana('');
  };
  const effectiveProtocol: ConnectionProtocol = kind === 'UPSTREAM_REGISTRAR' ? 'REST' : protocol;
  const restAdapters = ctx.rest_adapters.filter((a) => a.kinds.includes(kind));
  const ianaNumber = Number(iana);
  const codeOk = /^[a-z0-9][a-z0-9-]{1,31}$/.test(code);
  const upstreamOk = kind !== 'UPSTREAM_REGISTRAR' || (legalName.trim() && tradeName.trim() && Number.isInteger(ianaNumber) && ianaNumber > 0);
  const valid = codeOk && name.trim() && upstreamOk && reason.trim() && confirmed;
  const missing = [
    !codeOk && t('create.code'),
    !name.trim() && t('create.name'),
    !upstreamOk && t('upstream.title'),
    !reason.trim() && t('create.reasonLabel'),
    !confirmed && t('create.confirmLabel'),
  ].filter(Boolean) as string[];

  const submit = async () => {
    const res = await write.run({
      code,
      name: name.trim(),
      kind,
      protocol: effectiveProtocol,
      adapter: effectiveProtocol === 'EPP' ? eppAdapter : restAdapter === 'none' ? null : restAdapter,
      api_spec_reference: spec.trim() || null,
      upstream: kind === 'UPSTREAM_REGISTRAR' ? { legal_name: legalName.trim(), trade_name: tradeName.trim(), iana_id: ianaNumber } : null,
      reason: reason.trim(),
    });
    if (res) onCreated();
  };

  if (write.state.phase === 'done') {
    const res: WriteResult<ConnectionDetail> = write.state.result;
    return (
      <div className="space-y-4">
        <WriteOutcome result={res.result} approval={res.approval} warnings={res.warnings} appliedText={t('create.created')} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
          <Button onClick={() => navigate(`/settings/registries/${encodeURIComponent(res.item.code)}/environments?setup=endpoint`)}>
            {t('create.openNew')} <ArrowRight />
          </Button>
        </DialogFooter>
      </div>
    );
  }

  const webnicChosen = effectiveProtocol === 'REST' && restAdapter === WEBNIC_ADAPTER;
  return (
    <div className="space-y-4">
      {presets.length ? (
        <div className="grid gap-2">
          <span className="text-sm font-medium">{t('create.presetLabel')}</span>
          <div className="grid gap-2 sm:grid-cols-2">
            {presets.map((p) => (
              <ChoiceCard
                key={p.key}
                selected={preset === p.key}
                onSelect={() => applyPreset(p)}
                title={
                  <span className="inline-flex items-center gap-1.5">
                    <Wand2 className="size-3.5 text-muted-foreground" aria-hidden="true" /> {t(`create.preset.${p.key}.title`)}
                  </span>
                }
                body={t(`create.preset.${p.key}.body`)}
              />
            ))}
          </div>
          <p className="text-xs text-muted-foreground">{t('create.presetHint')}</p>
        </div>
      ) : null}
      <div className="grid gap-2">
        <span className="text-sm font-medium">{t('create.kindLabel')}</span>
        <div className="grid gap-2 sm:grid-cols-2">
          <ChoiceCard selected={kind === 'REGISTRY'} onSelect={() => setKind('REGISTRY')} title={t('kind.REGISTRY')} body={t('kindHint.REGISTRY')} />
          <ChoiceCard selected={kind === 'UPSTREAM_REGISTRAR'} onSelect={() => setKind('UPSTREAM_REGISTRAR')} title={t('kind.UPSTREAM_REGISTRAR')} body={t('kindHint.UPSTREAM_REGISTRAR')} />
        </div>
      </div>
      <div className="grid gap-2">
        <span className="text-sm font-medium">{t('create.protocolLabel')}</span>
        <div className="grid gap-2 sm:grid-cols-2">
          <ChoiceCard selected={effectiveProtocol === 'EPP'} onSelect={() => setProtocol('EPP')} title={t('protocol.EPP')} body={t('protocolHint.EPP')} disabled={kind === 'UPSTREAM_REGISTRAR'} />
          <ChoiceCard selected={effectiveProtocol === 'REST'} onSelect={() => setProtocol('REST')} title={t('protocol.REST')} body={t('protocolHint.REST')} />
        </div>
        {kind === 'UPSTREAM_REGISTRAR' ? <p className="text-xs text-muted-foreground">{t('create.upstreamRest')}</p> : null}
        <p className="text-xs text-muted-foreground">{t('create.channelPreview')} <ChannelBadge channel={channelOf(kind, effectiveProtocol)} /></p>
      </div>
      <div className="grid items-start gap-3 sm:grid-cols-2">
        <Field label={t('create.code')} hint={code && !codeOk ? <span className="text-critical">{t('create.codeInvalid')}</span> : t('create.codeHint')}>
          <Input value={code} onChange={(e) => setCode(e.target.value.toLowerCase())} autoComplete="off" spellCheck={false} className="font-mono" maxLength={32} />
        </Field>
        <Field label={t('create.name')}>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
        </Field>
        {effectiveProtocol === 'EPP' ? (
          <SelectField
            label={t('create.adapter')}
            value={eppAdapter}
            onChange={setEppAdapter}
            options={[
              { value: 'standard-epp', label: t('adapter.standard-epp') },
              { value: 'vnnic-epp', label: t('adapter.vnnic-epp') },
            ]}
            hint={t('create.adapterHint')}
          />
        ) : (
          <SelectField
            label={t('create.adapter')}
            value={restAdapter}
            onChange={setRestAdapter}
            options={[{ value: 'none', label: t('adapter.none') }, ...restAdapters.map((a) => ({ value: a.key, label: tx(`adapter.${a.key}`, a.key) }))]}
            hint={restAdapters.length ? undefined : t('create.noRestAdapters')}
          />
        )}
        <Field label={t('create.spec')} hint={t('create.specHint')}>
          <Input value={spec} onChange={(e) => setSpec(e.target.value)} maxLength={300} />
        </Field>
      </div>
      {webnicChosen ? (
        <Callout tone="info" title={t('webnic.createTitle')}>
          <ul className="list-disc space-y-0.5 pl-4">
            <li>
              {t('webnic.createAddresses')} <code className="text-xs">{WEBNIC_BASE_URLS.OTE}</code> · <code className="text-xs">{WEBNIC_BASE_URLS.PRODUCTION}</code>
            </li>
            <li>{t('webnic.createAuth')}</li>
            <li>{t('webnic.createIana')}</li>
          </ul>
        </Callout>
      ) : null}
      {webnicChosen ? <WebnicAllowListNote /> : null}
      {effectiveProtocol === 'REST' && restAdapter === 'none' ? (
        <Callout tone="warn" title={t('adapterNotice.title')}>
          {t('adapterNotice.body')}
        </Callout>
      ) : null}
      {kind === 'UPSTREAM_REGISTRAR' ? (
        <fieldset className="grid items-start gap-3 rounded-lg border p-3 sm:grid-cols-2">
          <legend className="px-1 text-sm font-medium">{t('upstream.title')}</legend>
          <p className="text-xs text-muted-foreground sm:col-span-2">{t('upstream.hint')}</p>
          <Field label={t('upstream.legalName')} className="sm:col-span-2">
            <Input value={legalName} onChange={(e) => setLegalName(e.target.value)} maxLength={300} />
          </Field>
          <Field label={t('upstream.tradeName')}>
            <Input value={tradeName} onChange={(e) => setTradeName(e.target.value)} maxLength={300} />
          </Field>
          <Field label={t('upstream.ianaId')} hint={iana && !(Number.isInteger(ianaNumber) && ianaNumber > 0) ? <span className="text-critical">{t('upstream.ianaInvalid')}</span> : undefined}>
            <Input value={iana} onChange={(e) => setIana(e.target.value.replace(/\D/g, ''))} inputMode="numeric" />
          </Field>
        </fieldset>
      ) : null}
      <p className="text-xs text-muted-foreground">{t('create.nextSteps')}</p>
      <ReasonField value={reason} onChange={setReason} />
      <ConfirmCheck checked={confirmed} onChange={setConfirmed} text={t('create.confirm')} />
      {write.state.phase === 'error' ? <IntegrationError error={write.state.error} /> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button type="button" onClick={() => void submit()} disabled={!valid || write.busy}>
          <Plus /> {t('create.submit')}
        </Button>
      </DialogFooter>
      {!valid && missing.length ? (
        <p className="text-right text-xs text-muted-foreground" role="status">
          {t('create.missing', { fields: missing.join(', ') })}
        </p>
      ) : null}
    </div>
  );
}
